import {
  Component,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  effect,
  afterRenderEffect,
  afterNextRender,
  Injector,
  viewChildren,
  untracked,
  ElementRef,
  DestroyRef,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HuxleyComponent } from '../huxley/huxley';
import { ActionGlyphComponent } from '../action-glyph/action-glyph';
import { AiGeneratingStatusComponent } from '../ai-generating-status/ai-generating-status';
import { getRecommendationStatusVariant } from '../../utils/status-indicator.utils';
import { SelectComponent } from '../select/select';
import { ButtonComponent } from '../button/button';
import { ChangeDecisionComponent } from '../change-decision/change-decision';
import { IconComponent } from '../icon/icon';
import { SkeletonDirective } from '../../shared/directives';
import { QuestionsStructure } from '../../services/questionnaire-version/questionnaire-version.service';
import { AiRecommendation, RecommendationStatus } from '../../models';
import {
  QUESTIONNAIRE_ANSWER_PREFIX,
  QUESTIONNAIRE_ANSWER_PREFIX_LENGTH,
  QUESTIONNAIRE_ANSWER_NOT_ANSWERED,
  QUESTIONNAIRE_ANSWER_OTHER_NOT_SPECIFIED,
} from '../../utils/assessment.utils';

export interface VisibilityCondition {
  questionId: string;
  operator: 'equals' | 'not_equals' | 'includes' | 'not_includes';
  value: string | string[];
}

export interface Question {
  id: string;
  type: 'text' | 'textarea' | 'radio' | 'checkbox' | 'dropdown';
  question: string;
  helpText?: string;
  /** Help text used when no AI suggestion is coming for this question —
   *  see `ApiQuestion.help_text_unassisted`. Only the pre-questionnaire
   *  question sets one; the paged questionnaire always uses `helpText`. */
  helpTextUnassisted?: string;
  options?: Array<{ value: string; label: string }>;
  hasOther?: boolean;
  phase: number;
  section?: string;
  sectionDescription?: string;
  visibleIf?: VisibilityCondition;
  /** `false` = may be left blank without blocking page completion.
   *  Undefined is treated as required — mapped from the API's
   *  `required` column, which defaults to `true` when absent. */
  required?: boolean;
}

interface QuestionnaireAnswers {
  [questionId: string]: string | string[] | null;
}

interface ReviewSummary {
  question: string;
  answer: string;
  questionId: string;
  phase: number;
  section?: string;
  type: Question['type'];
}

interface ReviewSection {
  phase: number;
  section?: string;
  sectionDescription?: string;
  questions: ReviewSummary[];
}

interface PageGroup {
  section?: string;
  sectionDescription?: string;
  phase: number;
  questions: Question[];
}

/** The exact suggestion the user reviewed, for a guarded server-side dismissal. */
export interface RecommendationDismissRequest {
  questionId: string;
  recommendation: AiRecommendation;
}

/**
 * Baked, render-ready view of one question's AI suggestion.
 *
 * Built once per `recommendations` / `regeneratingQuestionIds` change so
 * the template does a single map lookup per question instead of calling
 * three helpers per row on every change-detection cycle.
 *
 * `state` is the whole rendering decision:
 *   * `loading`    — a targeted re-run is in flight for this question, so
 *                    the stale suggestion must NOT be shown (a user would
 *                    read the old answer as the new one).
 *   * `suggestion` — at least one token resolved to a real answer option
 *                    (or the question is free-text). Applicable.
 *   * `empty`      — a suggestion exists but nothing in it maps to an
 *                    option of this question. Rendered as "no suggestion"
 *                    and NOT applicable — see `_findOption`.
 *   * `dismissed`  — server-persisted decision; keep only the refresh affordance.
 */
interface RecommendationView {
  state: 'loading' | 'suggestion' | 'empty' | 'dismissed' | 'applied';
  /** Option labels (never raw identifiers) joined for display. */
  displayValue: string;
  reasoning: string;
  /** `medium` confidence — the agent flagged thin input. Rendered with
   *  weaker wording and a visible caveat so a tentative answer
   *  can't read as a confident one. */
  isTentative: boolean;
}

/**
 * What the user asked the agent to re-do. Maps 1:1 onto the three modes of
 * `POST /assessments/{id}/recommendations`:
 *
 *   * `{ scope: 'unanswered' }` — the default bulk run. Only questions with
 *     no stored answer; everything the user has answered is left alone and
 *     used as grounding.
 *   * `{ scope: 'all' }` — start over. The only mode that replaces
 *     suggestions for answered questions, and the only one that gives up
 *     cross-question grounding, so the copy offering it has to say so.
 *   * `{ questionIds }` — the per-question "re-suggest this one".
 */
export type RegenerateSuggestionsRequest =
  | { scope: 'unanswered' | 'all' }
  | { questionIds: readonly string[] };

@Component({
  selector: 'app-questionnaire',
  imports: [
    MatTooltipModule,
    SelectComponent,
    ButtonComponent,
    ChangeDecisionComponent,
    IconComponent,
    MatRadioModule,
    MatCheckboxModule,
    MatIconModule,
    HuxleyComponent,
    ActionGlyphComponent,
    AiGeneratingStatusComponent,
    TranslocoModule,
    SkeletonDirective,
  ],
  templateUrl: './questionnaire.html',
  styleUrl: './questionnaire.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionnaireComponent {
  questions = input.required<Question[]>();
  /** Phase-0 questions, excluded from `questions` because the create
   *  wizard owns them. Rendered read-only on the review page so the
   *  answer that grounded every AI suggestion stays visible. Empty for
   *  questionnaires without a pre-questionnaire phase (`ai_risk`). */
  preQuestionnaireQuestions = input<Question[]>([]);
  questionsStructure = input<QuestionsStructure | null>(null); // Optional: if not provided, will try to infer from questions
  loading = input<boolean>(false);
  answers = input<QuestionnaireAnswers>({});
  recommendations = input<Record<string, AiRecommendation> | null>(null);
  recommendationStatus = input<RecommendationStatus | null>(null);
  recommendationIssue = input<'trigger' | 'connection' | 'timeout' | null>(null);
  recommendationScope = input<readonly string[]>([]);
  dismissingQuestionId = input<string | null>(null);
  dismissRequested = output<RecommendationDismissRequest>();
  private readonly _dismissalFocusId = signal<string | null>(null);
  private readonly _recommendationRefreshButtons =
    viewChildren<ButtonComponent>('recommendationRefresh');

  readonly recommendationDetailsExpanded = signal(true);
  readonly recommendationVariant = computed(() =>
    getRecommendationStatusVariant(this.recommendationStatus(), !!this.recommendationIssue())
  );

  readonly recommendationOutcome = computed(() => {
    const issue = this.recommendationIssue();
    if (issue) return `questionnaire.recommendation_${issue}_failed`;
    if (this.isSuggestionsGenerating()) return null;
    if (this.recommendationStatus() === 'failed') return 'questionnaire.recommendation_job_failed';
    if (this.recommendationStatus() !== 'completed') return null;
    const scope = this.recommendationScope();
    const hasSuggestions = [...this.recommendationViews()].some(
      ([id, view]) => view.state === 'suggestion' && (!scope.length || scope.includes(id))
    );
    const hasDismissed = [...this.recommendationViews()].some(
      ([id, view]) => view.state === 'dismissed' && (!scope.length || scope.includes(id))
    );
    const hasApplied = [...this.recommendationViews()].some(
      ([id, view]) => view.state === 'applied' && (!scope.length || scope.includes(id))
    );
    if (!hasSuggestions && hasApplied) return 'questionnaire.recommendation_applied_summary';
    if (!hasSuggestions && hasDismissed) return 'questionnaire.recommendation_dismissed_summary';
    return `questionnaire.recommendation_${scope.length ? 'scoped_' : ''}${hasSuggestions ? 'ready' : 'empty'}`;
  });
  /** Question ids the in-flight re-run will answer — the BE's
   *  `requested_question_ids`. Their cards render the waiting row instead
   *  of the stale suggestion; every other card is untouched, which is the
   *  whole reason the BE echoes the ids back. */
  regeneratingQuestionIds = input<readonly string[]>([]);
  /** A re-run has been fired but its `202` hasn't landed yet, so the
   *  affected ids aren't known. Drives the buttons' busy state only —
   *  `regeneratingQuestionIds` scopes the cards. */
  regenerationPending = input<boolean>(false);
  /** Human-readable names of what the last suggestion pass actually read
   *  (`AssessmentRecord.recommendation_context_sources`) — answers "what
   *  was this based on?" in the UI instead of in documentation. */
  recommendationContextSources = input<readonly string[]>([]);
  /** Whether to offer the re-run actions at all. Off for questionnaires
   *  that never had suggestions, for types the route rejects, and for
   *  read-only assessments. */
  canRegenerateSuggestions = input<boolean>(false);
  disabled = input<boolean>(false);
  generateDisabled = input<boolean>(false);
  hasExistingAssessment = input<boolean>(false);
  assessmentType = input<string | null>(null); // Assessment type (e.g., 'AI_RISK_ASSESSMENT')
  answersChange = output<QuestionnaireAnswers>();
  reviewConfirmed = output<QuestionnaireAnswers>();
  closeRequested = output<void>();
  /** The user asked for suggestions to be re-run — see
   *  {@link RegenerateSuggestionsRequest}. The parent owns the API call,
   *  the autosave flush that must precede it, and the polling. */
  regenerateRequested = output<RegenerateSuggestionsRequest>();
  navigateToReview = input<number>(0);

  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _injector = inject(Injector);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _transloco = inject(TranslocoService);
  private readonly _translationVersion = signal(0);

  private _answers = signal<QuestionnaireAnswers>({});
  private _showHelpText = signal<{ [key: string]: boolean }>({});
  private _currentPage = signal<number>(0);
  private _questionsPerPage = signal<number>(3);
  private _frozenQuestionsPerPage: number | null = null;

  private _hasCheckedInitialState = signal<boolean>(false);
  private readonly _isInEditMode = signal(false);
  protected readonly isInEditMode = this._isInEditMode.asReadonly();

  private _isVisible(question: Question, answers: QuestionnaireAnswers): boolean {
    const cond = question.visibleIf;
    if (!cond) return true;
    const answer = answers[cond.questionId];
    const vals = Array.isArray(cond.value) ? cond.value : [cond.value];
    switch (cond.operator) {
      case 'equals':
        return answer === cond.value;
      case 'not_equals':
        return answer !== cond.value;
      case 'includes':
        return Array.isArray(answer)
          ? vals.some(v => answer.includes(v))
          : vals.includes(answer as string);
      case 'not_includes':
        return Array.isArray(answer)
          ? !vals.some(v => answer.includes(v))
          : !vals.includes(answer as string);
      default:
        return true;
    }
  }

  private readonly _visibleQuestionIds = computed<Set<string>>(() => {
    const answers = this._answers();
    const visible = new Set<string>();
    for (const q of this.questions()) {
      if (this._isVisible(q, answers)) {
        visible.add(q.id);
      }
    }
    return visible;
  });

  readonly visibleQuestions = computed(() =>
    this.questions().filter(q => this._visibleQuestionIds().has(q.id))
  );

  // Computed property to check if review buttons should be disabled
  // Edits are now unlimited, so we only check if component is disabled
  public readonly areReviewButtonsDisabled = computed(() => {
    return this.disabled() || this.generateDisabled();
  });

  // Computed property for button text
  public readonly generateButtonText = computed(() => {
    this._translationVersion();
    return this._transloco.translate(
      this.hasExistingAssessment()
        ? 'questionnaire.regenerate_assessment'
        : 'questionnaire.generate_assessment'
    );
  });

  // Computed property to format assessment type for display
  public readonly formattedAssessmentType = computed(() => {
    const type = this.assessmentType();
    if (!type) return null;
    // Convert 'AI_RISK_ASSESSMENT' to 'AI Risk Assessment'
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  });

  constructor() {
    afterRenderEffect(() => {
      const id = this._dismissalFocusId();
      if (!id || this.dismissingQuestionId() !== null) return;
      const dismissed = this.recommendationViews().get(id)?.state === 'dismissed';
      this._dismissalFocusId.set(null);
      if (dismissed) {
        this._recommendationRefreshButtons()
          .find(button => button.ariaControls() === `recommendation-${id}`)
          ?.focus();
      }
    });

    this._transloco.langChanges$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._translationVersion.update(v => v + 1));

    // Initialize answers from input
    effect(() => {
      const inputAnswers = this.answers();
      if (Object.keys(inputAnswers).length > 0) {
        this._answers.set(inputAnswers);
      }
    });

    // Check if all questions are answered and navigate to review on initial load
    // Watch both input answers and internal answers to catch when data is loaded
    effect(() => {
      const inputAnswers = this.answers();
      const internalAnswers = this._answers();
      const questions = this.questions();
      const pages = this.pages();

      // Only check once on initial load when we have answers, questions, and pages are computed
      // Make sure internal answers match input answers (synced)
      const hasAnswers =
        Object.keys(inputAnswers).length > 0 || Object.keys(internalAnswers).length > 0;
      const answersSynced =
        Object.keys(inputAnswers).length === 0 ||
        JSON.stringify(inputAnswers) === JSON.stringify(internalAnswers);

      if (
        !this._hasCheckedInitialState() &&
        hasAnswers &&
        questions.length > 0 &&
        pages.length > 0 &&
        answersSynced
      ) {
        // Use a small delay to ensure computed signals are fully updated
        setTimeout(() => {
          if (this.areAllPagesComplete() && this._currentPage() < this.totalPages()) {
            this.goToPage(this.totalPages());
          }
          this._hasCheckedInitialState.set(true);
        }, 50);
      }
    });

    effect(() => {
      const trigger = this.navigateToReview();
      if (trigger > 0) {
        untracked(() => this.goToPage(this.totalPages()));
      }
    });

    // ResizeObserver for dynamic questions per page
    if (isPlatformBrowser(this._platformId)) {
      const observer = new ResizeObserver(entries => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          const overhead = 180; // pagination + header
          const perQuestion = 180;
          const count = Math.floor((height - overhead) / perQuestion);
          const clamped = Math.min(5, Math.max(2, count));
          this._questionsPerPage.set(clamped);
        }
      });
      observer.observe(this._elementRef.nativeElement);
      this._destroyRef.onDestroy(() => observer.disconnect());
    }
  }

  getAnswer(questionId: string): string | string[] | null {
    const answer = this._answers()[questionId];
    return answer !== undefined ? answer : null;
  }

  updateAnswer(questionId: string, value: string | string[]): void {
    this._answers.update(answers => {
      const updated = {
        ...answers,
        [questionId]: value,
      };
      // Emit changes after update
      this.answersChange.emit(updated);
      return updated;
    });
  }

  getRecommendation(questionId: string): AiRecommendation | null {
    return this.recommendations()?.[questionId] ?? null;
  }

  applyRecommendation(questionId: string): void {
    const rec = this.getRecommendation(questionId);
    if (
      !rec ||
      rec.dismissed ||
      !rec.value?.trim() ||
      this.disabled() ||
      this.dismissingQuestionId() === questionId
    )
      return;

    const value = this.recommendationAnswer(
      this.questions().find(q => q.id === questionId),
      rec.value
    );
    if (value === null) return;
    this.updateAnswer(questionId, value);
    // The decision controls disappear once the answer matches: keep keyboard focus in this question.
    afterNextRender(
      () => {
        const region = document.getElementById(`answer-region-${questionId}`);
        const control =
          document.getElementById(questionId) ??
          region?.querySelector<HTMLElement>('input:checked') ??
          region?.querySelector<HTMLElement>('input,textarea,button');
        control?.focus();
      },
      { injector: this._injector }
    );
  }

  private recommendationAnswer(
    question: Question | undefined,
    value: string
  ): string | string[] | null {
    if (!question?.options?.length) return value;
    if (question.type === 'checkbox') {
      const mapped = this._splitRecommendationTokens(value)
        .map(part => this._findOption(question, part)?.value)
        .filter((option): option is string => option !== undefined);
      return mapped.length ? mapped : null;
    }
    if (question.type === 'radio' || question.type === 'dropdown')
      return this._findOption(question, value.trim())?.value ?? null;
    return value;
  }

  private recommendationMatchesAnswer(
    question: Question | undefined,
    value: string,
    answer: string | string[] | null | undefined
  ): boolean {
    const suggested = this.recommendationAnswer(question, value);
    if (Array.isArray(suggested))
      return (
        Array.isArray(answer) &&
        JSON.stringify([...new Set(answer)].sort()) ===
          JSON.stringify([...new Set(suggested)].sort())
      );
    return suggested !== null && typeof answer === 'string' && answer.trim() === suggested.trim();
  }

  private _splitRecommendationTokens(value: string): string[] {
    return value
      .split(/[,|]/)
      .map(part => part.trim())
      .filter(Boolean);
  }

  /**
   * Resolve one recommended token to an answer option — exact value match
   * first, then case-insensitive label match.
   *
   * Returns `undefined` when the token matches nothing, and every caller
   * DROPS it rather than falling back to the raw text. The model has been
   * observed inventing identifiers (`users_of_services`, `website_visitors`
   * on `S3`, which has six fixed options): displaying them leaks internal-
   * looking strings, and applying them writes values no checkbox can
   * render — invisible in the UI, but persisted, carried into the report,
   * and handed back to the agent on a re-run as a *confirmed* answer.
   *
   * Unmapped tokens are deliberately not routed into the question's free-
   * text "Other" either: an invented identifier is not evidence the user
   * meant free text.
   */
  private _findOption(
    question: Question,
    raw: string
  ): { value: string; label: string } | undefined {
    if (!question.options?.length) return undefined;
    const byValue = question.options.find(o => o.value === raw);
    if (byValue) return byValue;
    const lower = raw.toLowerCase();
    return question.options.find(o => o.label.toLowerCase() === lower);
  }

  /** Human-readable labels for a recommendation — resolves
   *  "generative|automation" → "Generative AI, Process automation / RPA
   *  with AI". Unmapped tokens are dropped; an all-unmapped value yields
   *  `''`, which the view model renders as "no suggestion". */
  private _resolveDisplayValue(question: Question | undefined, value: string | null): string {
    const raw = value?.trim();
    if (!raw) return '';
    if (!question?.options?.length) return raw;
    return this._splitRecommendationTokens(raw)
      .map(part => this._findOption(question, part)?.label)
      .filter((label): label is string => label !== undefined)
      .join(', ');
  }

  private readonly _regeneratingIds = computed(() => new Set(this.regeneratingQuestionIds()));

  /** Render-ready suggestion per question id — see {@link RecommendationView}. */
  readonly recommendationViews = computed<ReadonlyMap<string, RecommendationView>>(() => {
    const views = new Map<string, RecommendationView>();
    const regenerating = this._regeneratingIds();
    const questionsById = new Map(this.questions().map(q => [q.id, q]));

    for (const id of regenerating) {
      const previous = this.recommendations()?.[id];
      views.set(id, {
        state: 'loading',
        displayValue: this._resolveDisplayValue(questionsById.get(id), previous?.value ?? null),
        reasoning: previous?.reasoning ?? '',
        isTentative: previous?.confidence === 'medium',
      });
    }

    for (const [id, rec] of Object.entries(this.recommendations() ?? {})) {
      if (regenerating.has(id)) continue; // a stale suggestion must not outlive its re-run
      const displayValue = this._resolveDisplayValue(questionsById.get(id), rec?.value ?? null);
      let state: RecommendationView['state'] = 'empty';
      if (rec?.dismissed) state = 'dismissed';
      else if (displayValue)
        state = this.recommendationMatchesAnswer(
          questionsById.get(id),
          rec?.value ?? '',
          this._answers()[id]
        )
          ? 'applied'
          : 'suggestion';
      views.set(id, {
        state,
        displayValue,
        reasoning: rec?.reasoning ?? '',
        isTentative: !rec?.dismissed && rec?.confidence === 'medium',
      });
    }

    return views;
  });

  recommendationView(questionId: string): RecommendationView | null {
    return this.recommendationViews().get(questionId) ?? null;
  }

  /** Any re-run in flight — either already scoped to ids, or fired and
   *  waiting on the `202` that will name them. Every re-run control is
   *  disabled while this is true: a second call would just take the BE's
   *  `409`. */
  readonly isRegenerating = computed(
    () => this.regenerationPending() || this._regeneratingIds().size > 0
  );

  /** Any suggestion pass in flight — a scoped re-run *or* a page-level
   *  `pending` (initial generation, unscoped / 409 resume). Re-run
   *  buttons disable on this so they stay inert for the whole wait, not
   *  only while `isRegenerating` is set. */
  readonly isSuggestionsGenerating = computed(
    () =>
      this.isRegenerating() ||
      (!this.recommendationIssue() &&
        (this.recommendationStatus() === 'pending' ||
          this.recommendationStatus() === 'claim_pending'))
  );

  readonly isRecommendationBusy = computed(
    () => this.isSuggestionsGenerating() || this.dismissingQuestionId() !== null
  );

  requestDismissRecommendation(questionId: string): void {
    const recommendation = this.getRecommendation(questionId);
    if (
      this.disabled() ||
      !this.canRegenerateSuggestions() ||
      this.isRecommendationBusy() ||
      !recommendation?.value ||
      recommendation.dismissed
    )
      return;
    this._dismissalFocusId.set(questionId);
    this.dismissRequested.emit({ questionId, recommendation });
  }

  /** Bulk re-run. `unanswered` (the default) leaves the user's own answers
   *  alone; `all` asks for everything again, including questions they have
   *  already answered. */
  requestRegenerate(scope: 'unanswered' | 'all'): void {
    if (this.disabled() || this.isRecommendationBusy()) return;
    this.regenerateRequested.emit({ scope });
  }

  /** Per-question "re-suggest this one". */
  requestRegenerateQuestion(questionId: string): void {
    if (this.disabled() || this.isRecommendationBusy()) return;
    this.regenerateRequested.emit({ questionIds: [questionId] });
  }

  toggleHelpText(questionId: string): void {
    this._showHelpText.update(show => ({
      ...show,
      [questionId]: !show[questionId],
    }));
  }

  isHelpTextVisible(questionId: string): boolean {
    return this._showHelpText()[questionId] || false;
  }

  /**
   * Paginate the visible questions **within each section**.
   *
   * A page takes its phase badge and section heading from its first
   * question, so a page spanning two sections would render its tail
   * questions under the wrong phase heading. Splitting on section
   * boundaries first makes the heading correct by construction.
   *
   * Consequence worth knowing: sections shorter than `perPage` get their
   * own short page rather than being packed with the next section's
   * questions, so the questionnaire has more pages than a flat slice
   * would produce. Exact page splits still can't be dictated — `perPage`
   * is derived from viewport height via the `ResizeObserver` below.
   */
  private getGroupedQuestions(): PageGroup[] {
    const perPage = this._frozenQuestionsPerPage ?? this._questionsPerPage();
    const pages: PageGroup[] = [];
    let run: Question[] = [];

    const flushRun = (): void => {
      for (let i = 0; i < run.length; i += perPage) {
        const pageQuestions = run.slice(i, i + perPage);
        const first = pageQuestions[0];
        pages.push({
          section: first.section,
          sectionDescription: first.sectionDescription,
          phase: first.phase,
          questions: pageQuestions,
        });
      }
      run = [];
    };

    for (const question of this.visibleQuestions()) {
      const previous = run[run.length - 1];
      if (
        previous &&
        (previous.phase !== question.phase || previous.section !== question.section)
      ) {
        flushRun();
      }
      run.push(question);
    }
    flushRun();

    return pages;
  }

  readonly pages = computed(() => this.getGroupedQuestions());
  readonly totalPages = computed(() => this.pages().length);
  readonly currentPageData = computed(() => this.pages()[this._currentPage()] ?? null);

  /** Precomputed question ID -> 1-based index map. O(1) lookup per call. */
  private readonly _questionNumberMap = computed(() => {
    const map = new Map<string, number>();
    this.visibleQuestions().forEach((q, i) => map.set(q.id, i + 1));
    return map;
  });

  readonly progressPercentage = computed(() => {
    const total = this.totalPages();
    if (total === 0) return 0;
    if (this.showReview()) return 100;
    return Math.round(((this._currentPage() + 1) / total) * 100);
  });

  readonly progressLabel = computed(() => {
    this._translationVersion();
    if (this.showReview()) return this._transloco.translate('questionnaire.review');
    return this._transloco.translate('questionnaire.progress_label', {
      current: this._currentPage() + 1,
      total: this.totalPages(),
    });
  });

  readonly canGoPrevious = computed(() => {
    // Can go back from review page
    if (this.showReview()) return true;
    return this._currentPage() > 0;
  });

  readonly isCurrentPageComplete = computed(() => {
    const currentPage = this.currentPageData();
    if (!currentPage) return false;
    // Access answers signal to make this reactive
    const answers = this._answers();
    return currentPage.questions.every(q => this.isAnswerComplete(answers[q.id], q));
  });

  getQuestionNumber(questionId: string): number {
    return this._questionNumberMap().get(questionId) ?? 0;
  }

  readonly canGoNext = computed(() => this.isCurrentPageComplete());

  readonly isOnFinalPage = computed(() => {
    return this._currentPage() >= this.totalPages() - 1;
  });

  readonly shouldShowReviewButton = computed(() => {
    return this.isOnFinalPage() && this.isCurrentPageComplete();
  });

  readonly showReview = computed(() => {
    return this._currentPage() >= this.totalPages();
  });

  goToPage(pageIndex: number): void {
    // Freeze questions per page after first navigation to prevent mid-session re-pagination
    if (pageIndex > 0 && this._frozenQuestionsPerPage === null) {
      this._frozenQuestionsPerPage = this._questionsPerPage();
    }

    // Allow going to review page (totalPages) if all pages are complete
    const maxPage = this.totalPages();
    if (pageIndex >= 0 && pageIndex <= maxPage) {
      // If trying to go to review, check all pages are complete
      if (pageIndex === maxPage) {
        if (!this.areAllPagesComplete()) {
          return;
        }
      }
      this._currentPage.set(pageIndex);
      const scrollContainer = this._elementRef.nativeElement.querySelector('.questionnaire-page');
      if (scrollContainer) {
        scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  areAllPagesComplete(): boolean {
    return this.pages().every(page =>
      page.questions.every(q => this.isAnswerComplete(this.getAnswer(q.id), q))
    );
  }

  private isAnswerComplete(
    answer: string | string[] | null | undefined,
    question?: Question
  ): boolean {
    // `required: false` questions may be left blank — an unanswered one
    // must not block the page. A *partially* answered optional question
    // (e.g. "Other" picked with no description) still blocks: the user
    // opted in, so the follow-up is owed.
    const optional = question?.required === false;
    if (answer === null || answer === undefined) return optional;
    const prefix = QUESTIONNAIRE_ANSWER_PREFIX;
    const prefixLen = QUESTIONNAIRE_ANSWER_PREFIX_LENGTH;

    if (typeof answer === 'string') {
      const trimmed = answer.trim();
      if (trimmed === '') return optional;
      // "Other" free-text (radio/dropdown): the specified text is required.
      if (trimmed.startsWith(prefix)) {
        return trimmed.substring(prefixLen).trim() !== '';
      }
      // "Other" selected but not yet described (stored as the bare `other`).
      if (question?.hasOther && trimmed === 'other') return false;
      return true;
    }

    if (Array.isArray(answer)) {
      if (answer.length === 0) return optional;
      if (question?.hasOther) {
        const otherSelected = answer.some(
          a => typeof a === 'string' && (a === 'other' || a.startsWith(prefix))
        );
        if (otherSelected) {
          const otherText = answer.find(a => typeof a === 'string' && a.startsWith(prefix));
          if (!otherText || otherText.substring(prefixLen).trim() === '') return false;
        }
      }
    }
    return true;
  }

  goToPrevious(): void {
    if (this.canGoPrevious()) {
      this.goToPage(this._currentPage() - 1);
    }
  }

  goToNext(): void {
    if (this.canGoNext()) {
      this.goToPage(this._currentPage() + 1);
    }
  }

  /** Render one stored answer as display text — resolves option values to
   *  their labels and unwraps the `other:` free-text prefix. */
  private formatAnswerText(
    question: Question,
    answer: string | string[] | null | undefined
  ): string {
    if (answer === null || answer === undefined) {
      return QUESTIONNAIRE_ANSWER_NOT_ANSWERED;
    }
    if (typeof answer === 'string') {
      if (answer.startsWith(QUESTIONNAIRE_ANSWER_PREFIX)) {
        return (
          answer.substring(QUESTIONNAIRE_ANSWER_PREFIX_LENGTH).trim() ||
          QUESTIONNAIRE_ANSWER_OTHER_NOT_SPECIFIED
        );
      }
      // Find the label for this value
      const option = question.options?.find(opt => opt.value === answer);
      return option ? option.label : answer;
    }
    return answer
      .map(a => {
        if (typeof a === 'string' && a.startsWith(QUESTIONNAIRE_ANSWER_PREFIX)) {
          return (
            a.substring(QUESTIONNAIRE_ANSWER_PREFIX_LENGTH).trim() ||
            QUESTIONNAIRE_ANSWER_OTHER_NOT_SPECIFIED
          );
        }
        const option = question.options?.find(opt => opt.value === a);
        return option ? option.label : a;
      })
      .join(', ');
  }

  private getReviewSummary(): ReviewSummary[] {
    const summary: ReviewSummary[] = [];
    const pages = this.pages();
    const answers = this._answers();

    pages.forEach(page => {
      page.questions.forEach(question => {
        summary.push({
          question: question.question,
          answer: this.formatAnswerText(question, answers[question.id]),
          questionId: question.id,
          phase: question.phase,
          section: question.section,
          type: question.type,
        });
      });
    });

    return summary;
  }

  /**
   * Read-only recap of the pre-questionnaire answers (phase 0), shown at
   * the top of the review page.
   *
   * These questions are answered in the create wizard, not here — they
   * ground the AI's suggestions for everything below them, so letting
   * them be edited on the review page would silently desync the rest of
   * the questionnaire's suggestions from the text they were built on.
   * Visible, not editable.
   */
  readonly preQuestionnaireSummary = computed<readonly ReviewSummary[]>(() => {
    const answers = this._answers();
    return this.preQuestionnaireQuestions().map(question => ({
      question: question.question,
      answer: this.formatAnswerText(question, answers[question.id]),
      questionId: question.id,
      phase: question.phase,
      section: question.section,
      type: question.type,
    }));
  });

  readonly reviewSectionsSummary = computed<ReviewSection[]>(() => {
    const summary = this.getReviewSummary();
    const sectionsMap = new Map<string, ReviewSection>();
    const allQuestions = this.visibleQuestions();

    summary.forEach(item => {
      const sectionKey = item.section || `Phase ${item.phase}`;

      if (!sectionsMap.has(sectionKey)) {
        const page = this.pages().find(
          p =>
            p.section === item.section ||
            (p.questions.length > 0 && p.questions[0].phase === item.phase && !p.section)
        );

        sectionsMap.set(sectionKey, {
          phase: item.phase,
          section: item.section,
          sectionDescription: page?.sectionDescription,
          questions: [],
        });
      }

      const section = sectionsMap.get(sectionKey)!;
      section.questions.push(item);
    });

    // Sort sections by phase, then by their position in the original structure
    const sortedSections = Array.from(sectionsMap.values()).sort((a, b) => {
      if (a.phase !== b.phase) {
        return a.phase - b.phase;
      }

      // Get section positions from the original structure
      const getSectionPosition = (phase: number, sectionName?: string): number => {
        if (!sectionName) return 999; // Sections without names go last

        const structure = this.questionsStructure();
        if (!structure) return 999; // If no structure provided, use default position

        const phaseData = structure[phase];
        if (!phaseData) return 999;

        const section = phaseData[sectionName];
        return section?.position ?? 999;
      };

      const positionA = getSectionPosition(a.phase, a.section);
      const positionB = getSectionPosition(b.phase, b.section);

      return positionA - positionB;
    });

    // Sort questions within each section by their original order
    sortedSections.forEach(section => {
      section.questions.sort((a, b) => {
        const indexA = allQuestions.findIndex(q => q.id === a.questionId);
        const indexB = allQuestions.findIndex(q => q.id === b.questionId);
        return indexA - indexB;
      });
    });

    return sortedSections;
  });

  confirmReview(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.reviewConfirmed.emit(this._answers());
  }

  goBackToEdit(): void {
    this._isInEditMode.set(true);
    this.goToPage(this.totalPages() - 1);
  }

  onTextChange(questionId: string, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.updateAnswer(questionId, target.value);
  }

  onSelectionChange(questionId: string, value: string): void {
    this.updateAnswer(questionId, value);
  }

  onCheckboxChange(questionId: string, value: string, checked: boolean): void {
    const currentAnswer = this.getAnswer(questionId);
    let newValue: string[];

    if (Array.isArray(currentAnswer)) {
      newValue = checked ? [...currentAnswer, value] : currentAnswer.filter(v => v !== value);
    } else {
      newValue = checked ? [value] : [];
    }

    this.updateAnswer(questionId, newValue);
  }

  onOtherTextChange(questionId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    const currentAnswer = this.getAnswer(questionId);
    const otherValue = `${QUESTIONNAIRE_ANSWER_PREFIX}${target.value}`;

    if (Array.isArray(currentAnswer)) {
      // Remove any existing "other:" values and add new one
      const filtered = currentAnswer.filter(v => !v.startsWith(QUESTIONNAIRE_ANSWER_PREFIX));
      this.updateAnswer(questionId, target.value ? [...filtered, otherValue] : filtered);
    } else {
      // For non-array answers, update with the other value or empty string
      this.updateAnswer(questionId, target.value ? otherValue : '');
    }
  }

  isOtherChosen(questionId: string): boolean {
    const answer = this.getAnswer(questionId);
    if (Array.isArray(answer)) {
      return answer.some(a => a === 'other' || a.startsWith(QUESTIONNAIRE_ANSWER_PREFIX));
    }
    if (typeof answer === 'string') {
      return answer === 'other' || answer.startsWith(QUESTIONNAIRE_ANSWER_PREFIX);
    }
    return false;
  }

  isChecked(questionId: string, value: string): boolean {
    const answer = this.getAnswer(questionId);
    if (Array.isArray(answer)) {
      return answer.includes(value);
    }
    return answer === value;
  }

  /**
   * Options to render for a question. When `hasOther` is set the component
   * renders its own free-text "Other" row, so a backend-provided `other`
   * option is dropped here to avoid showing two "Other" entries.
   */
  readonly dropdownOptions = computed(() => {
    this._translationVersion();
    return new Map(
      this.questions().map(question => {
        const options = this.displayOptions(question);
        return [
          question.id,
          question.hasOther
            ? [
                ...options,
                {
                  value: 'other',
                  label:
                    this.otherOptionLabel(question) ??
                    this._transloco.translate<string>('questionnaire.other_label_dropdown'),
                },
              ]
            : options,
        ] as const;
      })
    );
  });

  onDropdownChange(id: string, value: string | string[] | null): void {
    if (typeof value === 'string') this.onSelectionChange(id, value);
  }

  singleSelectionValue(id: string): string | null {
    if (this.isOtherChosen(id)) return 'other';
    const value = this.getAnswer(id);
    return typeof value === 'string' ? value : null;
  }

  displayOptions(question: Question): Array<{ value: string; label: string }> {
    const options = question.options ?? [];
    // When the question has a free-text "Other", the component renders its own
    // "Other" row, so drop the backend's `other` option to avoid a duplicate.
    return question.hasOther ? options.filter(option => option.value !== 'other') : options;
  }

  /**
   * Label for the free-text "Other" row. Prefers the backend `other` option's
   * label (e.g. "Other — please describe in the text field below"); callers
   * fall back to a generic i18n label when the backend omits it.
   */
  otherOptionLabel(question: Question): string | null {
    return question.options?.find(option => option.value === 'other')?.label ?? null;
  }

  getOtherValue(questionId: string): string {
    const answer = this.getAnswer(questionId);
    if (!answer) return '';
    if (Array.isArray(answer)) {
      const other = answer.find(v => v.startsWith(QUESTIONNAIRE_ANSWER_PREFIX));
      return other ? other.replace(QUESTIONNAIRE_ANSWER_PREFIX, '') : '';
    }
    if (typeof answer === 'string' && answer.startsWith(QUESTIONNAIRE_ANSWER_PREFIX)) {
      return answer.replace(QUESTIONNAIRE_ANSWER_PREFIX, '');
    }
    return '';
  }
}
